import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../api/endpoints';
import { formatXPUNT, formatDate } from '../utils/helpers';
import { 
  FaUser, FaEnvelope, FaWallet, FaTrophy, 
  FaStar, FaEdit, FaSave, FaTimes,
  FaTwitter, FaLink, FaCopy, FaCheck,
  FaChartLine, FaUsers, FaGift, FaCrown,
  FaRocket, FaArrowRight
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './Profile.css';

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    display_name: '',
    bank_account: '',
    local_currency: 'NGN',
  });
  const [stats, setStats] = useState({
    betCodes: 0,
    competitions: 0,
    h2hGames: 0,
    winRate: 0,
    totalPoints: 0,
  });
  const [copied, setCopied] = useState(false);

  // ==================== SYNC FORM WITH USER ====================
  useEffect(() => {
    if (user) {
      setFormData({
        display_name: user.display_name || '',
        bank_account: user.bank_account || '',
        local_currency: user.local_currency || 'NGN',
      });
    }
  }, [user]);

  // ==================== HANDLE OAUTH REDIRECT CALLBACKS ====================
   useEffect(() => {
  const params = new URLSearchParams(location.search);

  const xConnected = params.get('x_connected');
  const xError = params.get('x_error');
  const fbConnected = params.get('fb_connected');
  const fbError = params.get('fb_error');

  if (!xConnected && !xError && !fbConnected && !fbError) return;

  if (xConnected === '1') {
    toast.success('X account connected!');
  }
  if (xError) {
    toast.error(`X connection failed: ${xError}`);
  }
  if (fbConnected === '1') {
    toast.success('Facebook account connected!');
  }
  if (fbError) {
    toast.error(`Facebook connection failed: ${fbError}`);
  }

  const doRefresh = async () => {
    try {
      if (typeof refreshUser === 'function') {
        await refreshUser();
      } else {
        const res = await userAPI.getProfile();
        if (res?.data) {
          updateUser(res.data);
        }
      }
    } catch (err) {
      console.warn('[Profile] refresh after OAuth failed:', err);
    }
  };
  doRefresh();

  navigate('/profile', { replace: true });
}, [location.search, navigate, refreshUser, updateUser]);

  // ==================== SAVE PROFILE ====================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await userAPI.updateProfile(formData);
      if (response.data.success) {
        updateUser(formData);
        setEditing(false);
        toast.success('Profile updated successfully');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // ==================== X CONNECT / DISCONNECT ====================
  const handleConnectX = async () => {
    try {
      const response = await userAPI.connectX();
      if (response.data?.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error(response.data?.message || 'Failed to connect X');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to connect X');
    }
  };

  const handleDisconnectX = async () => {
    if (!window.confirm('Disconnect your X account?')) return;
    try {
      const response = await userAPI.disconnectX();
      if (response.data?.success) {
        toast.success('X account disconnected');
        if (typeof refreshUser === 'function') {
          await refreshUser();
        } else {
          const res = await userAPI.getProfile();
          if (res?.data) updateUser(res.data);
        }
      } else {
        toast.error(response.data?.message || 'Failed to disconnect');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to disconnect');
    }
  };

  // ==================== FACEBOOK CONNECT / DISCONNECT ====================
  const handleConnectFacebook = async () => {
    try {
      const response = await userAPI.connectFacebook();
      if (response.data?.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error(response.data?.message || 'Failed to connect Facebook');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to connect Facebook');
    }
  };

  const handleDisconnectFacebook = async () => {
    if (!window.confirm('Disconnect your Facebook account?')) return;
    try {
      const response = await userAPI.disconnectFacebook();
      if (response.data?.success) {
        toast.success('Facebook account disconnected');
        if (typeof refreshUser === 'function') {
          await refreshUser();
        } else {
          const res = await userAPI.getProfile();
          if (res?.data) updateUser(res.data);
        }
      } else {
        toast.error(response.data?.message || 'Failed to disconnect');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to disconnect');
    }
  };

  // ==================== REFERRAL COPY ====================
  const copyReferralLink = () => {
    const link = user?.referral_link || `${window.location.origin}/register?ref=${user?.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 3000);
  };

  // ==================== GUARD ====================
  if (!user) {
    return (
      <div className="profile-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  const isPunterPlus = user?.punter_plus || false;
  const profileImage = user?.x_profile_image || user?.facebook_profile_image || null;

  // ==================== RENDER ====================
  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-avatar-wrapper">
          <div className="profile-avatar">
            {profileImage ? (
              <img
                src={profileImage}
                alt={user.display_name || 'Profile'}
                className="profile-avatar-img"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.parentElement) {
                    e.target.parentElement.textContent = user.display_name?.[0] || 'U';
                  }
                }}
              />
            ) : (
              user.display_name?.[0] || 'U'
            )}
          </div>
          {isPunterPlus && (
            <span className="punter-badge">⭐ Punter+</span>
          )}
        </div>
        <div className="profile-info">
          <h1>{user.display_name || user.username}</h1>
          <p className="profile-username">@{user.username}</p>
          <div className="profile-meta">
            <span><FaEnvelope /> {user.email}</span>
            {user.x_connected && (
              <span className="x-connected">
                <FaTwitter /> Connected to X
              </span>
            )}
          </div>
        </div>
        <div className="profile-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => setEditing(!editing)}
          >
            {editing ? <FaTimes /> : <FaEdit />}
            {editing ? 'Cancel' : 'Edit Profile'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="profile-stats">
        <div className="stat-card">
          <FaChartLine className="stat-icon" />
          <div className="stat-info">
            <span className="stat-value">{user.win_rate || 0}%</span>
            <span className="stat-label">Win Rate</span>
          </div>
        </div>
        <div className="stat-card">
          <FaWallet className="stat-icon" />
          <div className="stat-info">
            <span className="stat-value">{formatXPUNT(user.wallet_balance || 0)}</span>
            <span className="stat-label">Balance</span>
          </div>
        </div>
        <div className="stat-card">
          <FaTrophy className="stat-icon" />
          <div className="stat-info">
            <span className="stat-value">{user.total_points || 0}</span>
            <span className="stat-label">Points</span>
          </div>
        </div>
        <div className="stat-card">
          <FaStar className="stat-icon" />
          <div className="stat-info">
            <span className="stat-value">{isPunterPlus ? 'Active' : 'Inactive'}</span>
            <span className="stat-label">Punter+ Status</span>
          </div>
        </div>
      </div>

      {/* Edit Form */}
      {editing && (
        <div className="profile-edit">
          <h3>Edit Profile</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Display Name</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Bank Account</label>
              <input
                type="text"
                value={formData.bank_account}
                onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                placeholder="Enter your bank account details"
              />
            </div>
            <div className="form-group">
              <label>Currency</label>
              <select
                value={formData.local_currency}
                onChange={(e) => setFormData({ ...formData, local_currency: e.target.value })}
              >
                <option value="NGN">₦ NGN</option>
                <option value="GHS">₵ GHS</option>
                <option value="KES">KSh KES</option>
                <option value="ZAR">R ZAR</option>
                <option value="USD">$ USD</option>
                <option value="EUR">€ EUR</option>
                <option value="GBP">£ GBP</option>
              </select>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? <LoadingSpinner size="small" /> : <><FaSave /> Save Changes</>}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Referral Section */}
      <div className="profile-section">
        <h3><FaUsers /> Referral Program</h3>
        <div className="referral-info">
          <div className="referral-stats">
            <div className="ref-stat">
              <span className="ref-value">{user.total_referrals || 0}</span>
              <span className="ref-label">Total Referrals</span>
            </div>
            <div className="ref-stat">
              <span className="ref-value">{formatXPUNT(user.referral_earnings || 0)}</span>
              <span className="ref-label">Earnings</span>
            </div>
          </div>
          <div className="referral-link-box">
            <code>{user.referral_link || `${window.location.origin}/register?ref=${user.id}`}</code>
            <button onClick={copyReferralLink} className="copy-btn">
              {copied ? <FaCheck /> : <FaCopy />}
            </button>
          </div>
          <Link to="/referral" className="btn btn-secondary btn-sm">
            View Referral Dashboard
          </Link>
        </div>
      </div>

      {/* Social Connections */}
      <div className="profile-section">
        <h3><FaLink /> Social Connections</h3>

        {/* X */}
        <div className="social-connection-item">
          <div className="social-header">
            <FaTwitter className="social-icon x-icon" />
            <span>X (Twitter)</span>
          </div>
          {user.x_connected ? (
            <div className="x-connected-info">
              <div className="x-profile">
                {user.x_profile_image && (
                  <img
                    src={user.x_profile_image}
                    alt="X Profile"
                    className="x-avatar"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <div>
                  <strong>{user.x_name || user.x_username}</strong>
                  <span className="x-handle">@{user.x_username}</span>
                </div>
              </div>
              <button className="btn btn-danger btn-sm" onClick={handleDisconnectX}>
                Disconnect
              </button>
            </div>
          ) : (
            <div className="x-connect">
              <p>Connect your X account to personalize your profile</p>
              <button className="btn btn-primary btn-sm" onClick={handleConnectX}>
                <FaTwitter /> Connect X
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Creator Mode */}
      {isPunterPlus && (
        <div className="profile-section creator-section">
          <h3><FaCrown /> Creator Mode</h3>
          <div className="creator-info-box">
            <p>You are a Punter+ member! Activate Creator Mode to start earning from your content.</p>
            <div className="creator-actions">
              <Link to="/creator/dashboard" className="btn btn-primary">
                <FaRocket /> Go to Creator Dashboard
              </Link>
              {!user?.creator_subscription_active && (
                <button className="btn btn-secondary">
                  Activate Creator Mode
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Subscription Info */}
      {user.creator_subscription_active && (
        <div className="profile-section creator-badge">
          <h3>🎯 Creator Mode Active</h3>
          <div className="creator-info">
            <div className="creator-stats">
              <div>
                <span className="label">Price</span>
                <span className="value">{formatXPUNT(user.creator_subscription_price)}</span>
              </div>
              <div>
                <span className="label">Type</span>
                <span className="value">{user.creator_subscription_type}</span>
              </div>
              <div>
                <span className="label">Subscribers</span>
                <span className="value">{user.creator_total_subscribers || 0}</span>
              </div>
              <div>
                <span className="label">Earnings</span>
                <span className="value">{formatXPUNT(user.creator_subscription_earnings || 0)}</span>
              </div>
            </div>
            <Link to="/creator/dashboard" className="btn btn-primary btn-sm">
              Creator Dashboard
            </Link>
          </div>
        </div>
      )}

      {/* Upgrade to Punter+ */}
      {!isPunterPlus && (
        <div className="profile-section upgrade-section">
          <h3><FaStar /> Upgrade to Punter+</h3>
          <div className="upgrade-info-box">
            <p>Unlock premium features and take your betting to the next level</p>
            <ul className="upgrade-benefits">
              <li>✅ Create private bet codes</li>
              <li>✅ Create competitions</li>
              <li>✅ Create H2H games</li>
              <li>✅ Create rollover games</li>
              <li>✅ Priority support</li>
              <li>✅ Premium badge</li>
            </ul>
            <Link to="/subscription" className="btn btn-primary">
              <FaRocket /> Upgrade Now <FaArrowRight />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;